import {
  defer,
  EMPTY,
  identity,
  merge,
  MonoTypeOperatorFunction,
  Observable,
  of,
  race,
  throwError,
  timer,
} from 'rxjs';
import {
  catchError,
  concatMap,
  delay,
  filter,
  first,
  flatMap,
  mapTo,
  retryWhen,
  switchMap,
  tap,
  throttle,
} from 'rxjs/operators';

import {globalWindow} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {ExtendedError} from '../../../libs/shared/helpers/src/types/customErrors';
import {AppStore} from '../../../src/store/appStore';
import {flushCommentAsyncAction} from '../../../src/store/data/comments/saveCommentActions';
import {flushMessageAsyncAction} from '../../../src/store/data/messages/saveMessageActions';
import {getUserId} from '../../../src/store/data/teammates/teammatesSelectors';
import {taskStore} from '../../../srcCommon/tasks/taskStore';
import {fromOnlineEvent} from '../../rx/reachability';
import {flushAllOfflineTasksAsyncAction, loadOfflineTasksAsyncAction} from '../../tasks/taskActions';
import {reportToBugsnag} from '../bugsnag/bugsnag';
import {offlineDraftComments} from './offlineDraftComments';
import {offlineDraftMessages} from './offlineDraftMessages';
import {OfflineError} from './offlineError';

/*
 * Constants.
 */

const errorCooldownDelay = 5 * 60 * 1000;
const offlineRetryDelay = 5 * 1000;
const onlineEventDelay = 1000;

/*
 * API.
 */

export function setupOfflineContent(store: AppStore) {
  // Revive optimistic updates for offline tasks.
  try {
    store.dispatch(loadOfflineTasksAsyncAction());
  } catch (error) {
    reportToBugsnag(new ExtendedError('Failed to load offline tasks', error));
  }

  // Continuously flush offline content to the server.
  const flushOfflineContent$ = merge(
    flushOfflineMessages(store),
    flushOfflineComments(store),
    flushOfflineTasks(store),
  );
  const subscription = flushOfflineContent$.subscribe();
  return () => subscription.unsubscribe();
}

/*
 * Helpers.
 */

function flushOfflineMessages(store: AppStore) {
  // Observe offline message UIDs.
  return fromOfflineDraftUids(offlineDraftMessages).pipe(
    // Process the message UIDs serially.
    concatMap((messageUid) =>
      // Save the message to the server.
      flushMessage(store, messageUid).pipe(
        // Keep retrying if we're offline.
        retryOfflineError(),
        // Drop the message and report for any other errors as a last resort.
        catchError((error) => {
          reportToBugsnag(new ExtendedError('Failed to flush offline message', error));
          return EMPTY;
        }),
      ),
    ),
  );
}

function flushMessage(store: AppStore, messageUid: string): Observable<void> {
  return defer(() => store.dispatch(flushMessageAsyncAction(messageUid)));
}

function flushOfflineComments(store: AppStore) {
  return fromOfflineDraftUids(offlineDraftComments).pipe(
    concatMap((commentUid) =>
      flushComment(store, commentUid).pipe(
        retryOfflineError(),
        catchError((error) => {
          reportToBugsnag(new ExtendedError('Failed to flush offline comment', error));
          return EMPTY;
        }),
      ),
    ),
  );
}

function flushComment(store: AppStore, commentUid: string): Observable<void> {
  return defer(() => store.dispatch(flushCommentAsyncAction(commentUid)));
}

/** Emits the IDs of offline drafts in the given store over time. */
function fromOfflineDraftUids(
  offlineDrafts: typeof offlineDraftMessages | typeof offlineDraftComments,
): Observable<string> {
  return merge(
    // Fetch current IDs and flatten the array.
    defer(() => offlineDrafts.readAllIdsAsync()).pipe(flatMap(identity)),
    // Observe newly added IDs.
    offlineDrafts.addedIds$,
  );
}

function flushOfflineTasks(store: AppStore) {
  // Observe offline tasks.
  return fromOfflineTaskChanges(store).pipe(
    // Throttle to make sure only one action is in flight at a time.
    throttle(
      () =>
        flushAllOfflineTasks(store).pipe(
          // Keep retrying if we're offline.
          retryOfflineError(),
        ),
      {leading: true, trailing: true},
    ),
    // Report and cool down for any other errors as a last resort.
    retryWhen((errors$) =>
      errors$.pipe(
        tap((error) => {
          reportToBugsnag(new ExtendedError('Failed to flush offline tasks', error));
        }),
        delay(errorCooldownDelay),
      ),
    ),
  );
}

/** Emits initially and then every time a new task is added. */
function fromOfflineTaskChanges(store: AppStore): Observable<void> {
  const initialState = store.getState();
  const userId = getUserId(initialState);

  return merge(
    // Emit initially for existing tasks.
    of(undefined),
    // Observe newly saved tasks.
    taskStore.taskSaveEvents$.pipe(filter((event) => event.userId === userId)).pipe(mapTo(undefined)),
  );
}

function flushAllOfflineTasks(store: AppStore): Observable<void> {
  return defer(() => store.dispatch(flushAllOfflineTasksAsyncAction()));
}

function retryOfflineError<T>(): MonoTypeOperatorFunction<T> {
  return retryWhen((errors$) =>
    errors$.pipe(
      switchMap((error) => {
        // If we're offline, set a timer and listen for the browser to go online. Otherwise, re-throw.
        if (error instanceof OfflineError) {
          // On mobile we don't want to retry as long as we're not online
          if (isMobile()) {
            return fromOnlineEvent(globalWindow).pipe(first(), delay(onlineEventDelay));
          }
          return race(
            timer(offlineRetryDelay),
            fromOnlineEvent(globalWindow).pipe(first(), delay(onlineEventDelay)),
          );
        }
        return throwError(error);
      }),
    ),
  );
}
